import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ASSETS } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LogOut, RefreshCw, Trash2, Plus, ExternalLink, Save, RotateCcw } from "lucide-react";
import { toast, Toaster } from "sonner";
import { SERVICES } from "../data/services";

const STATUS_LABEL = { nou: "Nou", confirmat: "Confirmat", finalizat: "Finalizat", anulat: "Anulat" };
const STATUS_COLOR = {
  nou: "bg-brand-yellow text-brand-navy",
  confirmat: "bg-brand-green text-white",
  finalizat: "bg-brand-navy text-white",
  anulat: "bg-brand-red text-white",
};

const CMS_KEYS = [
  { key: "home_hero_title", label: "Titlu Acasă (slogan)" },
  { key: "home_hero_subtitle", label: "Subtitlu Acasă" },
  { key: "about_intro", label: "Intro Despre" },
  { key: "about_motto", label: "Motto" },
  { key: "volunteer_intro", label: "Intro Voluntariat" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("appointments");

  return (
    <div className="min-h-screen bg-warm-gradient">
      <Toaster position="top-center" />
      <header className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto container-x py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center rounded-full bg-brand-yellow w-11 h-11 overflow-hidden ring-2 ring-white/20">
              <img src={ASSETS.emblem} alt="" className="w-full h-full object-cover scale-110" />
            </span>
            <div>
              <div className="font-display font-bold text-lg">Panou administrare</div>
              <div className="text-xs text-white/70">{user?.name}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-white/80 text-sm hover:underline inline-flex items-center gap-1">
              <ExternalLink className="w-4 h-4" /> Vezi site-ul
            </Link>
            <Button variant="ghost" onClick={logout} className="text-white hover:bg-white/10" data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-2"/> Ieșire
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto container-x py-8">
        <Stats />

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-white border border-brand-yellow/40">
            <TabsTrigger value="appointments" data-testid="tab-appointments">Programări</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Servicii</TabsTrigger>
            <TabsTrigger value="cms" data-testid="tab-cms">Conținut (CMS)</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Galerie</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog</TabsTrigger>
          </TabsList>

          <TabsContent value="appointments" className="mt-6"><AppointmentsTab /></TabsContent>
          <TabsContent value="services" className="mt-6"><ServicesTab /></TabsContent>
          <TabsContent value="cms" className="mt-6"><CmsTab /></TabsContent>
          <TabsContent value="gallery" className="mt-6"><GalleryTab /></TabsContent>
          <TabsContent value="blog" className="mt-6"><BlogTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stats() {
  const [s, setS] = useState({ total: 0, nou: 0, confirmat: 0, finalizat: 0 });
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="admin-stats">
      <StatCard label="Total cereri" value={s.total} />
      <StatCard label="Noi" value={s.nou} accent="bg-brand-yellow" />
      <StatCard label="Confirmate" value={s.confirmat} accent="bg-brand-green text-white" />
      <StatCard label="Finalizate" value={s.finalizat} accent="bg-brand-navy text-white" />
    </div>
  );
}

function StatCard({ label, value, accent = "bg-white" }) {
  return (
    <div className={`${accent} rounded-2xl p-5 border border-brand-yellow/40 shadow-sm`}>
      <div className="text-xs uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1">{value}</div>
    </div>
  );
}

function AppointmentsTab() {  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/appointments").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success("Status actualizat");
      load();
    } catch { toast.error("Eroare la actualizare"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergeți această cerere?")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      toast.success("Șters");
      load();
    } catch { toast.error("Eroare la ștergere"); }
  };

  if (loading) return <div className="text-brand-navy/60">Se încarcă…</div>;
  if (!items.length) return <div className="bg-white rounded-2xl p-10 text-center text-brand-navy/60 border border-brand-yellow/40">Nicio cerere înregistrată încă.</div>;

  return (
    <div className="space-y-3" data-testid="appointments-list">
      <div className="flex justify-end">
        <Button variant="outline" onClick={load} size="sm"><RefreshCw className="w-4 h-4 mr-2"/>Reîncarcă</Button>
      </div>
      {items.map((a) => (
        <div key={a.id} className="bg-white rounded-2xl p-5 border border-brand-yellow/40 shadow-sm" data-testid={`appt-${a.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display font-bold text-brand-navy text-lg">{a.name}</h3>
                <Badge className={STATUS_COLOR[a.status] || "bg-gray-200"}>{STATUS_LABEL[a.status] || a.status}</Badge>
              </div>
              <div className="text-sm text-brand-navy/70 mt-1">📞 <a href={`tel:${a.phone}`} className="font-semibold hover:underline">{a.phone}</a> {a.email && <>· ✉ {a.email}</>}</div>
              <div className="text-sm text-brand-navy/85 mt-2"><strong>{a.service}</strong></div>
              <div className="text-sm text-brand-navy/70 mt-1">📅 {a.preferred_date} · 🕒 {a.preferred_time}</div>
              {a.message && <div className="mt-2 text-sm bg-brand-cream p-2 rounded-lg text-brand-navy/80">{a.message}</div>}
              <div className="text-xs text-brand-navy/50 mt-2">Cerere: {new Date(a.created_at).toLocaleString("ro-RO")}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={() => remove(a.id)} className="text-brand-red hover:bg-brand-red/10" data-testid={`appt-delete-${a.id}`}>
                <Trash2 className="w-4 h-4 mr-1" /> Șterge
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ServicesTab() {
  const [overrides, setOverrides] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/service-images")
      .then((r) => {
        const map = (r.data || []).reduce((a, x) => { a[x.slug] = x.image_url; return a; }, {});
        setOverrides(map);
        setDrafts(SERVICES.reduce((a, s) => { a[s.slug] = map[s.slug] || s.image; return a; }, {}));
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (slug) => {
    try {
      await api.put(`/admin/service-images/${slug}`, { image_url: drafts[slug] });
      toast.success("Imagine actualizată");
      load();
    } catch { toast.error("Eroare la salvare"); }
  };

  const reset = async (slug) => {
    try {
      await api.delete(`/admin/service-images/${slug}`);
      toast.success("Resetat la imaginea implicită");
      load();
    } catch { toast.error("Eroare"); }
  };

  if (loading) return <div className="text-brand-navy/60">Se încarcă…</div>;

  return (
    <div className="space-y-4" data-testid="services-admin">
      <div className="bg-white rounded-2xl p-5 border border-brand-yellow/40 text-sm text-brand-navy/75">
        Schimbați imaginea fiecărui serviciu. Dați un URL public (de exemplu de pe Imgur, Google Drive cu link public, sau orice site care găzduiește imagini). Apăsați <strong>Salvează</strong>. Pentru a reveni la imaginea implicită, apăsați <strong>Resetare</strong>.
      </div>
      {SERVICES.map((s) => {
        const isOverride = !!overrides[s.slug];
        return (
          <div key={s.slug} className="bg-white rounded-2xl p-5 border border-brand-yellow/40 shadow-sm grid sm:grid-cols-[160px_1fr_auto] gap-4 items-center" data-testid={`service-row-${s.slug}`}>
            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-brand-cream">
              <img src={drafts[s.slug] || s.image} alt={s.title} className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="font-display font-bold text-brand-navy">{s.title}</h3>
              <p className="text-xs text-brand-navy/55">{isOverride ? "Imagine personalizată" : "Imagine implicită"}</p>
              <Label className="mt-3 block text-xs text-brand-navy/65 font-semibold">URL imagine</Label>
              <Input
                value={drafts[s.slug] || ""}
                onChange={(e) => setDrafts((p) => ({ ...p, [s.slug]: e.target.value }))}
                placeholder="https://..."
                className="mt-1"
                data-testid={`service-image-input-${s.slug}`}
              />
            </div>
            <div className="flex flex-col gap-2 sm:items-end">
              <Button onClick={() => save(s.slug)} size="sm" className="bg-brand-navy hover:bg-brand-navyDeep text-white" data-testid={`service-save-${s.slug}`}>
                <Save className="w-4 h-4 mr-2" /> Salvează
              </Button>
              {isOverride && (
                <Button onClick={() => reset(s.slug)} size="sm" variant="outline" data-testid={`service-reset-${s.slug}`}>
                  <RotateCcw className="w-4 h-4 mr-2" /> Resetare
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function CmsTab() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all(CMS_KEYS.map((k) => api.get(`/cms/${k.key}`).then((r) => [k.key, r.data?.value || ""])))
      .then((entries) => setValues(Object.fromEntries(entries)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (key) => {
    try {
      await api.put(`/admin/cms/${key}`, { value: values[key] || "" });
      toast.success("Salvat");
    } catch { toast.error("Eroare la salvare"); }
  };

  if (loading) return <div className="text-brand-navy/60">Se încarcă…</div>;

  return (
    <div className="space-y-4" data-testid="cms-list">
      {CMS_KEYS.map((k) => (
        <div key={k.key} className="bg-white rounded-2xl p-5 border border-brand-yellow/40 shadow-sm">
          <Label className="text-brand-navy font-semibold">{k.label}</Label>
          <Textarea
            value={values[k.key] || ""}
            onChange={(e) => setValues((p) => ({ ...p, [k.key]: e.target.value }))}
            className="mt-2"
            rows={3}
            data-testid={`cms-${k.key}`}
          />
          <Button onClick={() => save(k.key)} size="sm" className="mt-3 bg-brand-navy hover:bg-brand-navyDeep text-white">
            <Save className="w-4 h-4 mr-2" /> Salvează
          </Button>
        </div>
      ))}
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");

  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/gallery", { image_url: url, caption, order: items.length });
      setUrl(""); setCaption("");
      load();
      toast.success("Adăugat");
    } catch { toast.error("Eroare la adăugare"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergeți această imagine?")) return;
    try { await api.delete(`/admin/gallery/${id}`); load(); toast.success("Șters"); }
    catch { toast.error("Eroare"); }
  };

  return (
    <div className="space-y-6" data-testid="gallery-admin">
      <form onSubmit={add} className="bg-white rounded-2xl p-5 border border-brand-yellow/40 shadow-sm grid sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2 space-y-2">
          <Label className="text-brand-navy font-semibold">URL imagine</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://..." required data-testid="gallery-url" />
        </div>
        <div className="sm:col-span-2 space-y-2">
          <Label className="text-brand-navy font-semibold">Descriere</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Ex: Cabinetul nostru" data-testid="gallery-caption" />
        </div>
        <Button type="submit" className="bg-brand-red hover:bg-brand-redSoft text-white" data-testid="gallery-add"><Plus className="w-4 h-4 mr-2"/>Adaugă</Button>
      </form>

      {!items.length ? (
        <div className="bg-white rounded-2xl p-10 text-center text-brand-navy/60 border border-brand-yellow/40">Nicio imagine în galerie încă.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl overflow-hidden border border-brand-yellow/40 shadow-sm">
              <img src={it.image_url} alt={it.caption} className="w-full aspect-[4/3] object-cover" />
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="text-sm text-brand-navy truncate">{it.caption}</div>
                <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="text-brand-red"><Trash2 className="w-4 h-4"/></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", content: "", cover_image: "", service_slug: "", published: false });

  const load = () => api.get("/blog?published_only=false").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blog", form);
      setForm({ title: "", slug: "", excerpt: "", content: "", cover_image: "", service_slug: "", published: false });
      load();
      toast.success("Articol creat");
    } catch (err) { toast.error(err.response?.data?.detail || "Eroare"); }
  };

  const togglePublish = async (p) => {
    try { await api.put(`/admin/blog/${p.id}`, { published: !p.published }); load(); }
    catch { toast.error("Eroare"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ștergeți articolul?")) return;
    try { await api.delete(`/admin/blog/${id}`); load(); }
    catch { toast.error("Eroare"); }
  };

  return (
    <div className="space-y-6" data-testid="blog-admin">
      <form onSubmit={submit} className="bg-white rounded-2xl p-5 border border-brand-yellow/40 shadow-sm grid sm:grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Titlu</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} required data-testid="blog-title"/></div>
        <div className="space-y-2"><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e)=>setForm({...form,slug:e.target.value})} required data-testid="blog-slug"/></div>
        <div className="sm:col-span-2 space-y-2"><Label>Imagine de copertă (URL)</Label><Input value={form.cover_image} onChange={(e)=>setForm({...form,cover_image:e.target.value})}/></div>
        <div className="sm:col-span-2 space-y-2">
          <Label>Asociere cu serviciu (opțional)</Label>
          <select
            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.service_slug}
            onChange={(e)=>setForm({...form,service_slug:e.target.value})}
            data-testid="blog-service-slug"
          >
            <option value="">— Niciunul —</option>
            <option value="profilaxie-igienizare">Profilaxie și igienizare dentară</option>
            <option value="tratamente-odontale">Tratamente odontale</option>
            <option value="profilaxie-parodontala">Profilaxie parodontală și igienizare profesională</option>
            <option value="reabilitare-orala">Reabilitare orală cu proteze</option>
            <option value="protetica-dentara">Protetică dentară</option>
            <option value="estetica-dentara">Estetică dentară</option>
            <option value="tratamente-canal-radicular">Tratamente de canal radicular</option>
            <option value="ortodontie">Ortodonție – în colaborare cu medicul ortodont</option>
            <option value="consultatii-copii">Consultații și tratamente stomatologice pentru copii</option>
          </select>
        </div>
        <div className="sm:col-span-2 space-y-2"><Label>Rezumat</Label><Textarea rows={2} value={form.excerpt} onChange={(e)=>setForm({...form,excerpt:e.target.value})} required/></div>
        <div className="sm:col-span-2 space-y-2"><Label>Conținut</Label><Textarea rows={6} value={form.content} onChange={(e)=>setForm({...form,content:e.target.value})} required data-testid="blog-content"/></div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <input id="published" type="checkbox" checked={form.published} onChange={(e)=>setForm({...form,published:e.target.checked})}/>
          <Label htmlFor="published">Publică imediat</Label>
        </div>
        <Button type="submit" className="sm:col-span-2 bg-brand-red hover:bg-brand-redSoft text-white" data-testid="blog-create"><Plus className="w-4 h-4 mr-2"/>Creează articol</Button>
      </form>

      {!items.length ? (
        <div className="bg-white rounded-2xl p-10 text-center text-brand-navy/60 border border-brand-yellow/40">Niciun articol încă.</div>
      ) : (
        <div className="space-y-3">
          {items.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl p-4 border border-brand-yellow/40 shadow-sm flex items-center justify-between gap-3">
              <div>
                <div className="font-display font-bold text-brand-navy">{p.title}</div>
                <div className="text-xs text-brand-navy/60">/{p.slug}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant={p.published ? "default" : "outline"} onClick={() => togglePublish(p)}>
                  {p.published ? "Publicat" : "Ciornă"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="text-brand-red"><Trash2 className="w-4 h-4"/></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
